import React, { useMemo, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Skeleton from 'components_new/atoms/Skeleton'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import SimpleTable from 'components_new/molecules/SimpleTable'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

import { useCustomKpis } from 'redux/hooks/Metrics'

import AddKpiDialog from './dialogs/AddKpiDialog'
import EditKpiDialog from './dialogs/EditKpiDialog'

import { getKpiFormulaNode, parseKpiVariableWithChildren } from './utils'
import { useMetricActions } from 'redux/actions/Metrics'
import { ICustomEconomyMetric } from 'types/GlobalMetric'
import { useFetchedKpiVariables } from 'redux/hooks/KpiVariables'
import { useTranslation } from 'language/translator'

const EconomyCustomMetrics = () => {
  const [variables, fetchingVariables] = useFetchedKpiVariables()
  const [customKpis, fetchingCustomKpis] = useCustomKpis()

  const {
    tryCreateCustomMetric,
    tryDeleteCustomMetric,
    tryRemoveMetricDefinition,
    tryUpdateMetricCalculation
  } = useMetricActions()

  const { t } = useTranslation()

  const [addKpi, setAddKpi] = useState<boolean>(false)
  const [editKpi, setEditKpi] = useState<ICustomEconomyMetric | null>(null)
  const [deleteKpi, setDeleteKpi] = useState<ICustomEconomyMetric | null>(null)

  const formattedVariables = useMemo(
    () => parseKpiVariableWithChildren(variables),
    [variables]
  )

  const loading = fetchingVariables || fetchingCustomKpis

  if (loading) {
    return <Skeleton variant="rounded" width="100%" height={98} />
  }

  return (
    <>
      <SimpleTable
        addRow={{
          onClick: () => setAddKpi(true)
        }}
        border={true}
        columns={[
          { title: t('global', 'name') },
          { title: t('global', 'formula') },
          { title: t('global', 'unit') },
          { align: 'right', title: t('global', 'actions') }
        ]}
        hover={true}
        rows={customKpis.map((kpi) => {
          return {
            cells: [
              <Box
                key={1}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Tag startIconName="TrendingUpOutlined" color="primary" />
                <Text variant="subtitle2">{kpi.title.sv}</Text>
              </Box>,
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  cursor: 'default',
                  p: 1
                }}
                key={2}
              >
                {kpi.custom_calculation_block ? (
                  getKpiFormulaNode(
                    kpi.custom_calculation_block,
                    variables,
                    customKpis
                  )
                ) : (
                  <Tag
                    color="warning"
                    label={t('entities', 'not_configured')}
                    startIconName="Error"
                  />
                )}
              </Box>,
              <>{kpi.unit}</>,
              <>
                <IconButton onClick={() => setEditKpi(kpi)} size="small">
                  <Icon name="Edit" />
                </IconButton>
                <Tooltip
                  title={
                    kpi.homepal
                      ? t('metrics', 'standard_metric_locked')
                      : !kpi.allow_delete
                          ? t('metrics', 'metric_in_use')
                          : ''
                  }
                >
                  {/*
                   * The span is required: a disabled button fires no pointer
                   * events, so without it the tooltip explaining why delete is
                   * unavailable never shows.
                   */}
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <IconButton
                      disabled={!kpi.allow_delete}
                      onClick={() => setDeleteKpi(kpi)}
                      size="small"
                    >
                      <Icon name="Delete" />
                    </IconButton>
                  </Box>
                </Tooltip>
              </>
            ]
          }
        })}
        size="small"
      />

      {/* ----- Dialogs ----- */}
      <AddKpiDialog
        formattedVariables={formattedVariables}
        open={addKpi}
        onClose={() => setAddKpi(false)}
        onSave={(body) => {
          tryCreateCustomMetric({ data: body })
          setAddKpi(false)
        }}
        variables={variables}
        templates={customKpis}
      />
      {editKpi ? (
        <EditKpiDialog
          formattedVariables={formattedVariables}
          kpi={editKpi}
          open={Boolean(editKpi)}
          onClose={() => setEditKpi(null)}
          onRemoveDefinition={() => {
            tryRemoveMetricDefinition(editKpi.id)
            setEditKpi(null)
          }}
          onSave={(body) => {
            tryUpdateMetricCalculation(editKpi.id, body)
            setEditKpi(null)
          }}
          variables={variables}
          templates={customKpis}
        />
      ) : null}
      {deleteKpi ? (
        <DeleteDialog
          open={Boolean(deleteKpi)}
          onClose={() => setDeleteKpi(null)}
          onDelete={(callback) => {
            tryDeleteCustomMetric(deleteKpi.id)
            setDeleteKpi(null)
            callback()
          }}
          title={deleteKpi.title.sv}
          type={t('metrics', 'metric', true)}
        />
      ) : null}
    </>
  )
}

export default EconomyCustomMetrics
